import { readStudioEnv } from './cogo-platform-config'

export type StudioDistribution = 'cloud' | 'appliance' | 'unknown'

export type StudioCapabilityKey =
  | 'platform-connection'
  | 'local-auth'
  | 'google-auth'
  | 'local-publish'
  | 'cloud-publish'
  | 'custom-domain'
  | 'billing'
  | 'analytics'
  | 'external-integrations'

export interface StudioPresentationIdentity {
  distribution: StudioDistribution
  studioSourceSha: string | null
  applianceReleaseId: string | null
}

/**
 * Why a probe FAILED, as a closed set the reader's language can be derived
 * from — not as a pre-rendered English sentence.
 *
 * The `error` variant used to carry `message: string`, authored in English
 * inside `studio-capability-probes.ts` and rendered verbatim by
 * `runtime-capability-panel.tsx`. Measured 2026-08-18 on a `ko` surface:
 * "Platform health read failed (404)" in the middle of a Korean settings
 * screen. `reasonCode` alone could not replace it — `'malformed-response'` is
 * emitted from two different sites with two different meanings, and one of
 * them carries the HTTP status — so the fix is a discriminated union that
 * keeps BOTH the site and its parameter, and lets the panel render the
 * sentence.
 *
 * This is deliberately a `code` and not a `message`: the panel's copy table is
 * a `Record<StudioProbeFailure['code'], …>`, so adding a failure site here is
 * a compile error at the one place that renders it, rather than a new English
 * string appearing on a localized screen.
 */
export type StudioProbeFailure =
  /** `GET /api/v1/health` answered with a non-2xx status. */
  | { code: 'platform-health-status'; httpStatus: number }
  /** `GET /api/v1/health` answered 2xx but the body was not `{status:'ok'}`. */
  | { code: 'platform-health-malformed' }
  /** The health request never completed (transport threw). */
  | { code: 'platform-unreachable' }
  /** A capability response was not admitted by the client-side shape check. */
  | { code: 'capability-response-rejected'; httpStatus: number }

export type StudioCapabilityObservation =
  | { state: 'available'; observedAt: string; evidenceRefs: string[] }
  | {
      state: 'unavailable'
      reasonCode: string
      /**
       * Authored by the PLATFORM, not by Studio (`payload.message` from the
       * capability endpoint). Studio cannot localize a sentence it did not
       * write, so this is still rendered verbatim — the honest limit of this
       * pass, recorded rather than papered over.
       */
      message: string
      observedAt: string
      evidenceRefs: string[]
    }
  | { state: 'unknown'; reasonCode: 'not-probed' | 'no-context' }
  | {
      state: 'error'
      reasonCode: 'probe-transport-failed' | 'malformed-response'
      failure: StudioProbeFailure
    }

export interface StudioPresentationProfile extends StudioPresentationIdentity {
  observedAt: string | null
  capabilities: Partial<Record<StudioCapabilityKey, StudioCapabilityObservation>>
}

type StudioIdentityEnv = Pick<ImportMetaEnv,
  'VITE_STUDIO_DISTRIBUTION' | 'VITE_STUDIO_SOURCE_SHA' | 'VITE_APPLIANCE_RELEASE_ID'>

function nonEmpty(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed || null
}

function readDistribution(value: string | undefined): StudioDistribution {
  return value === 'cloud' || value === 'appliance' || value === 'unknown' ? value : 'unknown'
}

export function readStudioPresentationIdentity(env?: Partial<StudioIdentityEnv>): StudioPresentationIdentity {
  return {
    distribution: readDistribution(env?.VITE_STUDIO_DISTRIBUTION ?? readStudioEnv('VITE_STUDIO_DISTRIBUTION')),
    studioSourceSha: nonEmpty(env?.VITE_STUDIO_SOURCE_SHA ?? readStudioEnv('VITE_STUDIO_SOURCE_SHA')),
    applianceReleaseId: nonEmpty(
      env?.VITE_APPLIANCE_RELEASE_ID ?? readStudioEnv('VITE_APPLIANCE_RELEASE_ID'),
    ),
  }
}

/**
 * True when this build is installed inside a closed network — an appliance —
 * and therefore cannot reach any origin but its own.
 *
 * A surface that dials out (custom domains, a hosted subscription, a GitHub
 * OAuth popup, a vendor mailbox) is not merely *slow* here; it is unreachable,
 * and the edge CSP (`connect-src 'self'`) refuses it before the request
 * leaves. Such a control must therefore say so *before* it is pressed, the
 * same rule this settings screen already applies to controls with nothing
 * behind them.
 *
 * Two signals, because they are set for different reasons and a build may
 * carry either: `VITE_STUDIO_DISTRIBUTION=appliance` is the deployment's
 * *identity* (what Workspace Settings reports), while
 * `VITE_COGO_APPLIANCE_OFFLINE` is the *build* switch that strips remote fonts
 * and pins the local preview renderer. Either one alone means the same thing
 * for a reader here: nothing outside this origin will answer.
 */
export function isClosedNetworkDeployment(
  env?: Partial<StudioIdentityEnv & { VITE_COGO_APPLIANCE_OFFLINE: string }>,
): boolean {
  if (readDistribution(env?.VITE_STUDIO_DISTRIBUTION ?? readStudioEnv('VITE_STUDIO_DISTRIBUTION')) === 'appliance') {
    return true
  }
  return (
    (env?.VITE_COGO_APPLIANCE_OFFLINE ?? readStudioEnv('VITE_COGO_APPLIANCE_OFFLINE')) === 'true'
  )
}

/**
 * True when this Studio build's declared distribution is `appliance`.
 *
 * The single predicate every appliance-only UI gate (route/nav derivation,
 * template catalog, …) reads instead of each consumer re-parsing
 * `VITE_STUDIO_DISTRIBUTION` for itself — one profile, not a flag per
 * surface. Unlike {@link isClosedNetworkDeployment}, this does NOT also
 * treat `VITE_COGO_APPLIANCE_OFFLINE` as appliance: that flag is a network
 * *reachability* signal (can also be set for a dev/offline cloud build),
 * while surface removal (commerce nav, routes, ecommerce Starters) must
 * gate on the deployment's actual declared *identity*.
 */
export function isApplianceDistribution(
  env?: Partial<Pick<StudioIdentityEnv, 'VITE_STUDIO_DISTRIBUTION'>>,
): boolean {
  return (
    readDistribution(env?.VITE_STUDIO_DISTRIBUTION ?? readStudioEnv('VITE_STUDIO_DISTRIBUTION')) ===
    'appliance'
  )
}

export function reduceStudioPresentationProfile(
  identity: StudioPresentationIdentity,
  input: {
    probes: Partial<Record<StudioCapabilityKey, StudioCapabilityObservation>>
    operations: Partial<Record<StudioCapabilityKey, StudioCapabilityObservation>>
  },
): StudioPresentationProfile {
  const capabilities = { ...input.probes, ...input.operations }
  const observedTimes = Object.values(capabilities).flatMap((observation) =>
    observation && 'observedAt' in observation ? [observation.observedAt] : [],
  )

  return {
    ...identity,
    capabilities,
    observedAt: observedTimes.sort().at(-1) ?? null,
  }
}
